import { useEffect, useState } from "react";
import {
  fetchAccompanyingPersonFee as apiFetchAccompanyingPersonFee,
  fetchConveyanceFeePercentage as apiFetchConveyanceFeePercentage,
  fetchPaperCount,
  fetchPaperDiscountRules as apiFetchPaperDiscountRules,
  fetchRegistrationFeeForSelection,
} from "../api/conferenceRegistrationApi";
import {
  calculateOutstandingAmounts as calculateOutstandingAmountsForRegistration,
  getPaperDiscountPercentage,
  hasSuccessfulPaymentRecord,
} from "../payment/paymentCalculations";
import { shouldApplyAbstractDiscount } from "../registration/registrationModules";

export const useRegistrationFees = ({
  conferenceApi,
  registrationBaseUrl,
  conferenceId,
  conferenceName,
  formData,
  abstractStepNumber,
  wantsToSubmitAbstract,
  existingPaperId,
  selectedFocusedSessions,
  accompanyingPersons,
  accompanyingPersonFee,
  setAccompanyingPersonFee,
  focusedSessionFees,
  conveyanceFeePercentage,
  hasPostPaymentCategoryOrTypeChange,
  setConveyanceFeePercentage,
}) => {
  const [registrationFee, setRegistrationFee] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeError, setFeeError] = useState(null);
  const [paperDiscountRules, setPaperDiscountRules] = useState([]);
  const [submittedPaperCount, setSubmittedPaperCount] = useState(0);

  const fetchRegistrationFee = async () => {
    if (!conferenceId || !formData.categoryId) {
      setRegistrationFee(null);
      return;
    }

    setFeeLoading(true);
    setFeeError(null);

    try {
      const matchingFee = await fetchRegistrationFeeForSelection({
        conferenceApi,
        conferenceId,
        categoryId: formData.categoryId,
        category: formData.category,
        registrationTypeId: formData.registrationTypeId,
        registrationType: formData.registrationType,
      });

      const isExistingPaidRegistration =
        formData.paid === true ||
        hasSuccessfulPaymentRecord(formData.payment) ||
        !!formData.paymentId;

      if (!matchingFee) {
        if (!isExistingPaidRegistration) {
          throw new Error("No fee configuration found for the selected category");
        }

        setFeeError(null);
        return;
      }

      setRegistrationFee(matchingFee);
      setFeeError(null);
    } catch (error) {
      console.error("Error fetching registration fee:", error);
      setFeeError(error.message);
      setRegistrationFee(null);
    } finally {
      setFeeLoading(false);
    }
  };

  const fetchAccompanyingPersonFee = async () => {
    if (!conferenceId) {
      setAccompanyingPersonFee(null);
      return;
    }

    try {
      const feeData = await apiFetchAccompanyingPersonFee({
        conferenceApi,
        conferenceId,
      });
      setAccompanyingPersonFee(feeData);
    } catch (error) {
      console.error("Error fetching accompanying person fee:", error);
      setAccompanyingPersonFee(null);
    }
  };

  const fetchConveyanceFeePercentage = async () => {
    if (!conferenceId) {
      setConveyanceFeePercentage(null);
      return;
    }

    try {
      const percentage = await apiFetchConveyanceFeePercentage({
        conferenceApi,
        conferenceId,
      });
      setConveyanceFeePercentage(percentage);
    } catch (error) {
      console.error("Error fetching conveyance fee percentage:", error);
      setConveyanceFeePercentage(null);
    }
  };

  const shouldApplyPaperDiscount = shouldApplyAbstractDiscount({
    abstractStepNumber,
    wantsToSubmitAbstract,
  });

  const currentPaperSequence = shouldApplyPaperDiscount
    ? Math.max(1, submittedPaperCount + (existingPaperId ? 0 : 1))
    : 0;

  const paperDiscountPercentage = getPaperDiscountPercentage(
    currentPaperSequence,
    paperDiscountRules
  );

  const calculateOutstandingAmounts = () =>
    calculateOutstandingAmountsForRegistration({
      registrationFee,
      formData,
      selectedFocusedSessions,
      accompanyingPersons,
      accompanyingPersonFee,
      focusedSessionFees,
      conveyanceFeePercentage,
      hasPostPaymentCategoryOrTypeChange,
    });

  useEffect(() => {
    if (conferenceId && formData.categoryId) {
      fetchRegistrationFee();
    }
  }, [conferenceId, formData.categoryId, formData.registrationTypeId]);

  useEffect(() => {
    if (!conferenceId) {
      setPaperDiscountRules([]);
      return;
    }

    const loadPaperDiscountRules = async () => {
      try {
        const normalized = await apiFetchPaperDiscountRules({
          conferenceApi,
          conferenceId,
        });
        setPaperDiscountRules(normalized);
      } catch (error) {
        console.error("Error fetching paper discount rules:", error);
        setPaperDiscountRules([]);
      }
    };

    loadPaperDiscountRules();
  }, [conferenceApi, conferenceId]);

  useEffect(() => {
    const email = formData.email?.trim();
    const conference = (formData.conference || conferenceName || "").trim();
    if (!email || !conference) {
      setSubmittedPaperCount(0);
      return;
    }

    const loadSubmittedPaperCount = async () => {
      try {
        const count = await fetchPaperCount({
          registrationBaseUrl,
          email,
          conference,
        });
        setSubmittedPaperCount(count);
      } catch (error) {
        console.error("Error fetching submitted paper count:", error);
        setSubmittedPaperCount(0);
      }
    };

    loadSubmittedPaperCount();
  }, [formData.email, formData.conference, conferenceName, registrationBaseUrl]);

  useEffect(() => {
    if (conferenceId) {
      fetchAccompanyingPersonFee();
      fetchConveyanceFeePercentage();
    }
  }, [conferenceId]);

  return {
    registrationFee,
    setRegistrationFee,
    feeLoading,
    feeError,
    paperDiscountRules,
    submittedPaperCount,
    shouldApplyPaperDiscount,
    currentPaperSequence,
    paperDiscountPercentage,
    calculateOutstandingAmounts,
    fetchRegistrationFee,
    fetchAccompanyingPersonFee,
    fetchConveyanceFeePercentage,
  };
};
